import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status, Refresh2 } from "iconsax-react";
import { Select, Input, DatePicker } from "@/shared/ui";
import type { Locale, DateRange } from "@/shared/utils";
import type { ItemRequestStatus } from "../../../types";

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  dateRange: DateRange | null;
  onDateRangeChange: (r: DateRange | null) => void;
  statusFilter: ItemRequestStatus | null;
  onStatusChange: (v: ItemRequestStatus | null) => void;
  locale: Locale;
  hasActiveFilters: boolean;
  onReset: () => void;
}

export default function RequestsFilter({
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  statusFilter,
  onStatusChange,
  locale,
  hasActiveFilters,
  onReset,
}: Props) {
  const { t } = useTranslation("WarehouseRequestsPage");
  const { t: tWarehouse } = useTranslation("WarehousePage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onDateRangeChange(v);
  }

  const statusOptions: { label: string; value: ItemRequestStatus | null }[] = [
    { label: t("filters.allStatuses"), value: null },
    { label: tWarehouse("requests.status.approved"), value: "approved" as ItemRequestStatus },
    { label: tWarehouse("requests.status.rejected"), value: "rejected" as ItemRequestStatus },
    { label: tWarehouse("requests.status.fulfilling"), value: "fulfilling" as ItemRequestStatus },
    { label: tWarehouse("requests.status.fulfilled"), value: "fulfilled" as ItemRequestStatus },
    { label: tWarehouse("requests.status.cancelled"), value: "cancelled" as ItemRequestStatus },
    { label: tWarehouse("requests.status.unavailable"), value: "unavailable" as ItemRequestStatus },
  ];

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-48">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={dateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.allTime")}
          isSmall
          hasPeriodPicker
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<ItemRequestStatus | null>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-48"
          />
        </div>

        {hasActiveFilters && (
          <button
            className="px-3 h-8 flex justify-center items-center gap-2 content-base-low cursor-pointer"
            onClick={onReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-sm">{t("filters.reset")}</span>
          </button>
        )}
      </div>

    </div>
  );
}
